import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useState } from 'react';
import { BaseLineDown } from '@/app/components/icons/BaseLineDown';
import { BaseLineOptimize } from '@/app/components/icons/BaseLineOptimize';
import { BaseLineRecognize } from '@/app/components/icons/BaseLineRecognize';
import { Button } from '@/app/components/ui/button';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { createI18nConfig } from '@/app/utils';
import { useLocale } from '@/i18n/routing';

export enum PasteMode {
  Recognition = 'recognition',
  Optimize = 'optimize',
}

const CLIPBOARD_PLACEHOLDER = createI18nConfig<string>(
  {
    en: `Paste to auto-format, e.g.,
    Subject: A girl in a sailor-style school uniform sitting on a wooden park bench
    Environment: Sunlight filtering through green leaves
    Style: animation`,
    ja: `ワンクリックで内容を貼り付け＆整形
    サブジェクト：セーラー服の少女が木製の公園のベンチに座っている
    環境：緑の葉から差し込む日差し
    スタイル：アニメーション`,
    de: `Mit einem Klick einfügen & formatieren
    Subjekt: Ein Mädchen in einer Matrosen-Schuluniform sitzt auf einer Holzbank im Park
    Umgebung: Sonnenlicht fällt durch grüne Blätter
    Stil: Animation`,
    ko: `클릭하여 내용을 붙여넣고 포맷하세요.
    주제: 공원의 나무 벤치에 앉아 있는 세일러복을 입은 소녀
    환경: 초록 잎 사이로 햇살이 스며드는 공원
    스타일: 애니메이션`,
    pt: `Cole o conteúdo e formate-o com um clique
    Sujeito: Uma garota de uniforme escolar estilo marinheiro sentada num banco de madeira no parque
    Ambiente: Luz do sol se entrelaçando nas folhas verdes
    Estilo: animação`,
    ru: `Вставьте содержимое и отформатируйте его одним кликом
    Субъект: девочка в школьной форме в матросском стиле, сидящая на деревянной скамейке в парке
    Окружающая среда: солнечный свет, пробивающийся сквозь зеленую листву
    Стиль: анимация`,
    fr: `Collez le contenu et formatez-le d'un seul clic
    Sujet : Une fille en uniforme scolaire de style marin assise sur un banc en bois dans un parc
    Environnement : La lumière du soleil filtrant à travers des feuilles vertes
    Style : Animation`,
    es: `Pegue el contenido y formatéelo con un clic
      Sujeto: Una chica con uniforme escolar de estilo marinero sentada en un banco de madera del parque
      Entorno: Luz solar filtrándose a través de hojas verdes
      Estilo: animación`,
  },
  `粘贴内容，一键格式化；示例：
    主体：一位女孩穿着飘逸的长裙，飞奔穿过金黄色的芦苇地
    环境：夕阳西下，微风拂过，芦苇轻轻摇曳
    风格：动漫2d画风`,
);

interface ClipboardPanelProps {
  isVisible: boolean;
  onClose: () => void;
  onPaste: (text: string, mode: PasteMode) => void;
  maxLength: number;
}

export const ClipboardPanel: FC<ClipboardPanelProps> = ({ isVisible, onClose, onPaste, maxLength }) => {
  const [text, setText] = useState('');
  const [pasteMode, setPasteMode] = useState<PasteMode>(PasteMode.Recognition);
  const locale = useLocale();
  const t = useTranslations();

  const handleConfirm = async () => {
    onPaste(text, pasteMode);
    onClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="absolute bottom-0 left-0 z-10 flex w-full flex-col rounded-t-[20px] rounded-b-10 border border-system-white12 bg-system-black p-4 backdrop-blur-[15px]">
      {/* Header */}
      <div className="mb-2.5 flex items-center justify-between">
        <h3 className="text-system-white64 text-xs">{t('create.proMode.clipboard.title')}</h3>
        <BaseLineDown className="cursor-pointer text-base" onClick={onClose} />
      </div>

      {/* Textarea */}
      <Textarea
        className="mb-3 min-h-[120px] flex-1 resize-none rounded-md border-none p-0 font-medium text-base text-system-white caret-system-blue02 placeholder:font-normal placeholder:text-base placeholder:text-system-white24 focus-visible:ring-0 focus-visible:ring-offset-0"
        placeholder={CLIPBOARD_PLACEHOLDER[SITE][locale]}
        value={text}
        maxLength={maxLength}
        onChange={(e) => {
          const { value } = e.target;
          if (value.length >= maxLength) {
            toast({
              type: 'warning',
              message: t('create.createForm.error.invalidLength', { limit: maxLength }),
            });
          }
          setText(value);
        }}
      />

      {/* Footer Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {[
            {
              mode: PasteMode.Recognition,
              label: t('create.proMode.clipboard.recognize'),
              icon: <BaseLineRecognize className="text-base" />,
            },
            {
              mode: PasteMode.Optimize,
              label: t('create.proMode.clipboard.optimize'),
              icon: <BaseLineOptimize className="text-base" />,
            },
          ].map((item) => (
            <Button
              key={item.mode}
              size="sm"
              variant="solid"
              className="h-8 gap-1.5 bg-system-white10 text-system-white text-xs enabled:hover:bg-system-white24 aria-selected:cursor-default aria-selected:bg-system-blue02/25 aria-selected:text-system-blue02 aria-selected:enabled:hover:bg-system-blue02/25"
              aria-selected={pasteMode === item.mode}
              onClick={() => setPasteMode(item.mode)}
              icon={item.icon}
            >
              {item.label}
            </Button>
          ))}
        </div>
        <Button
          size="sm"
          variant="solid"
          disabled={!text}
          className="h-8 bg-system-blue02 text-system-white disabled:bg-system-white10 disabled:text-system-white24 disabled:opacity-100"
          onClick={handleConfirm}
        >
          {t('create.proMode.clipboard.confirm')}
        </Button>
      </div>
    </div>
  );
};
